import { useEffect, useRef, useState } from "react";
import { Stethoscope, Loader2, Play, CheckCheck, History } from "lucide-react";
import { toast } from "sonner";
import { runStrategyDiagnosis, getStrategyDiagnosisStatus, getStrategyDiagnosis, applyStrategyDiagnosis } from "@/lib/api";
import { DiagnosiResult } from "./DiagnosiResult";

const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" }) : "");

export function DiagnosiPanel({ onProfileApplied }) {
  const [data, setData] = useState(null);
  const [status, setStatus] = useState({ running: false });
  const [applying, setApplying] = useState(false);
  const [confirmApply, setConfirmApply] = useState(false);
  const timer = useRef(null);

  const load = async (id) => {
    try { setData(await getStrategyDiagnosis(id)); } catch { /* silenzioso */ }
  };

  const poll = () => {
    clearInterval(timer.current);
    timer.current = setInterval(async () => {
      try {
        const st = await getStrategyDiagnosisStatus();
        setStatus(st);
        if (!st.running) {
          clearInterval(timer.current);
          if (st.error) toast.error(`Diagnosi fallita: ${st.error}`);
          else { toast.success("Diagnosi strategica pronta"); load(); }
        }
      } catch { /* riprova al prossimo giro */ }
    }, 4000);
  };

  useEffect(() => {
    load();
    getStrategyDiagnosisStatus().then((st) => { setStatus(st); if (st.running) poll(); }).catch(() => {});
    return () => clearInterval(timer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const run = async () => {
    try {
      await runStrategyDiagnosis();
      setStatus({ running: true });
      toast("Diagnosi avviata: l'AI sta leggendo tutti i tuoi reel (1-2 minuti)");
      poll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossibile avviare la diagnosi");
    }
  };

  const apply = async () => {
    if (!confirmApply) { setConfirmApply(true); return; }
    setApplying(true);
    try {
      const p = await applyStrategyDiagnosis(data.latest.id);
      onProfileApplied(p);
      toast.success("Carta d'identità aggiornata con la diagnosi");
    } catch { toast.error("Errore nell'applicare la diagnosi"); } finally { setApplying(false); setConfirmApply(false); }
  };

  const latest = data?.latest;
  const history = data?.history || [];

  return (
    <section className="border border-[#262626] rounded-xl bg-[#111111] p-6 space-y-5" data-testid="strategy-diagnosis">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="font-display font-bold text-white text-lg flex items-center gap-2"><Stethoscope size={18} style={{ color: "#D97706" }} /> Diagnosi strategica AI</h2>
          <p className="text-xs text-neutral-500 mt-1">L'AI legge TUTTI i reel analizzati (hook, format, tono, CTA, metriche reali, filiera) e deduce tesi, pain, nemico e anello rotto — come per Mik. Gira da sola il 1° del mese.</p>
          {latest && <p className="text-[0.68rem] text-neutral-600 mt-1" data-testid="strategy-diagnosis-meta">Ultima: {fmt(latest.created_at)} · {latest.n_analyzed} reel analizzati su {latest.n_reels} · {latest.trigger}</p>}
        </div>
        <div className="flex items-center gap-2">
          {history.length > 1 && (
            <div className="relative">
              <History size={13} className="absolute left-2 top-1/2 -translate-y-1/2 text-neutral-500" />
              <select value={latest?.id || ""} onChange={(e) => load(e.target.value)} data-testid="strategy-diagnosis-history" className="bg-black/40 border border-[#262626] rounded-md text-xs text-neutral-300 pl-7 pr-2 py-1.5">
                {history.map((h) => <option key={h.id} value={h.id}>{`${fmt(h.created_at)} · ${h.n_analyzed} reel`}</option>)}
              </select>
            </div>
          )}
          <button onClick={run} disabled={status.running} data-testid="strategy-diagnosis-run" className="inline-flex items-center gap-1.5 text-xs font-semibold text-black rounded-md px-3 py-1.5 disabled:opacity-60" style={{ background: "#D97706" }}>
            {status.running ? <Loader2 size={13} className="animate-spin" /> : <Play size={13} />}{status.running ? "In corso…" : latest ? "Rifai la diagnosi" : "Esegui diagnosi"}
          </button>
        </div>
      </div>

      {status.running && <p className="text-xs text-amber-500/90" data-testid="strategy-diagnosis-running">L'AI sta leggendo i tuoi reel… resta su questa pagina, il risultato compare da solo.</p>}
      {!latest && !status.running && <p className="text-sm text-neutral-500" data-testid="strategy-diagnosis-empty">Nessuna diagnosi ancora. Premi «Esegui diagnosi»: un'unica chiamata AI, pochi centesimi.</p>}

      {latest && <DiagnosiResult r={latest.result} />}

      {latest && (
        <div className="flex flex-wrap items-center gap-3 pt-2 border-t border-[#1f1f1f]">
          <button onClick={apply} disabled={applying} data-testid="strategy-diagnosis-apply" className={`inline-flex items-center gap-1.5 text-xs rounded-md px-3 py-1.5 border transition-colors ${confirmApply ? "text-black font-semibold" : "text-neutral-300 hover:text-white border-[#262626] hover:border-[#404040]"}`} style={confirmApply ? { background: "#16A34A", borderColor: "#16A34A" } : {}}>
            {applying ? <Loader2 size={13} className="animate-spin" /> : <CheckCheck size={13} />}{confirmApply ? "Confermi? Sovrascrive tesi, pain, nemico e format" : "Applica alla carta d'identità"}
          </button>
          {confirmApply && <button onClick={() => setConfirmApply(false)} data-testid="strategy-diagnosis-apply-cancel" className="text-xs text-neutral-500 hover:text-white">Annulla</button>}
          <p className="text-[0.68rem] text-neutral-600">La carta d'identità resta comunque modificabile a mano.</p>
        </div>
      )}
    </section>
  );
}
